import React from 'react';
import { Box, Container, Typography, Grid, useTheme, alpha } from '@mui/material';
import {
  Bolt,
  NotificationsActive,
  TrendingDown,
  AutoMode
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const proofBlocks = [
  {
    icon: Bolt,
    metric: '< 5 min',
    headline: 'Go live in under 5 minutes',
    body: 'Import your services, set your availability, and share your booking link — your practice is open for business.',
    color: 'primary'
  },
  {
    icon: NotificationsActive,
    metric: '29%',
    headline: 'Reduce no-shows by up to 29%',
    body: 'Automated reminders with AI-optimised timing reach clients when they actually read them.',
    footnote: 'Based on automated reminder research, JMIR',
    color: 'success'
  },
  {
    icon: TrendingDown,
    metric: '30–60 days',
    headline: 'Spot churn before it costs you',
    body: 'Health scores flag at-risk clients 30–60 days early so you can act while the relationship is still warm.',
    color: 'secondary'
  },
  {
    icon: AutoMode,
    metric: '80%',
    headline: 'Fewer manual tasks',
    body: 'Reminders, invoicing, re-engagement, and follow-ups run on autopilot — you focus on clients, not admin.',
    color: 'primary'
  }
];

const OutcomeProofSection = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        py: { xs: 10, md: 15 },
        position: 'relative',
        background: '#000000',
        overflow: 'hidden'
      }}
    >
      <Container maxWidth="lg">
        <Box textAlign="center" sx={{ mb: 10 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="overline"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 700,
                letterSpacing: 2,
                mb: 1,
                display: 'block'
              }}
            >
              Real Outcomes
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: '#ffffff',
                mb: 3,
                fontSize: { xs: '2.2rem', md: '3.2rem' },
                letterSpacing: '-0.04em'
              }}
            >
              The impact of stopping the juggle.
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.secondary,
                maxWidth: 640,
                mx: 'auto',
                fontWeight: 400,
                lineHeight: 1.6
              }}
            >
              Every feature ties back to fewer cancellations, better retention, or hours you get back each week.
            </Typography>
          </motion.div>
        </Box>

        <Grid container spacing={4}>
          {proofBlocks.map((block, index) => {
            const IconComponent = block.icon;
            const colorValue = theme.palette[block.color]?.main || theme.palette.primary.main;

            return (
              <Grid item xs={12} sm={6} md={3} key={block.headline}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Box
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: 4,
                      ...theme.customEffects?.glass,
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        borderColor: alpha(colorValue, 0.3),
                        background: alpha(colorValue, 0.03),
                      }
                    }}
                  >
                    <IconComponent
                      sx={{ fontSize: 40, color: colorValue, mb: 3 }}
                    />

                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 900,
                        color: '#ffffff',
                        mb: 1,
                        fontSize: { xs: '2.4rem', md: '2.8rem' },
                        lineHeight: 1
                      }}
                    >
                      {block.metric}
                    </Typography>

                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: '#ffffff',
                        mb: 1.5,
                        fontSize: '1.1rem'
                      }}
                    >
                      {block.headline}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        lineHeight: 1.6
                      }}
                    >
                      {block.body}
                    </Typography>

                    {block.footnote && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 2,
                          color: alpha(theme.palette.text.secondary, 0.5),
                          fontStyle: 'italic',
                          lineHeight: 1.4
                        }}
                      >
                        {block.footnote}
                      </Typography>
                    )}
                  </Box>
                </motion.div>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

export default OutcomeProofSection;
